import React from 'react';
import { getEnabledNetworks } from '../config/networks';
import { NetworkIcon } from './NetworkIcon';
import { FEATURES } from '../config/features';
import { trackPaymentEvent } from '../utils/analytics';

interface NetworkSelectorProps {
  selectedNetwork: string;
  onNetworkChange: (network: string) => void;
  disabled?: boolean;
}

const NetworkSelector: React.FC<NetworkSelectorProps> = ({ 
  selectedNetwork, 
  onNetworkChange,
  disabled = false 
}) => {
  const enabledNetworks = getEnabledNetworks();

  // If network selector feature is disabled, return null
  if (!FEATURES.networkSelector) {
    return null;
  }

  // If only one network is enabled, don't show selector
  if (enabledNetworks.length <= 1) {
    return null;
  }

  return (
    <div className="network-selector">
      <h3 className="network-selector-title">Select Network</h3>
      <div className="network-selector-grid">
        {enabledNetworks.map((networkConfig) => {
          const isSelected = selectedNetwork === networkConfig.network;
          const isDisabled = disabled || !networkConfig.enabled;
          
          return (
            <button
              key={networkConfig.network}
              onClick={() => {
                if (!isDisabled) {
                  onNetworkChange(networkConfig.network);
                  trackPaymentEvent('network_selected', { network: networkConfig.network });
                }
              }}
              className={`network-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
              disabled={isDisabled}
              aria-label={`Select ${networkConfig.name} network`}
              aria-pressed={isSelected}
            >
              <div className="network-option-content">
                <NetworkIcon 
                  network={networkConfig.network} 
                  size={32} 
                  className="network-icon"
                />
                <span className="network-name">{networkConfig.name}</span>
                {networkConfig.nativeCurrency && (
                  <span className="network-currency">
                    {networkConfig.nativeCurrency.symbol}
                  </span>
                )}
              </div>
              {isSelected && (
                <div className="network-selected-indicator">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="10" cy="10" r="10" fill="currentColor"/>
                    <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </button>
          );
        })}
      </div>
      
      <style jsx>{`
        .network-selector {
          margin: 1rem 0;
        }

        .network-selector-title {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
          color: var(--text-primary, #111);
        }

        .network-selector-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
          gap: 0.75rem;
        }

        .network-option {
          position: relative;
          padding: 1rem;
          border: 2px solid var(--border-color, #e5e7eb);
          border-radius: 0.75rem;
          background: var(--bg-primary, #fff);
          cursor: pointer;
          transition: all 0.2s ease;
          overflow: hidden;
        }

        .network-option:hover:not(.disabled) {
          border-color: var(--accent-color, #6366f1);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .network-option.selected {
          border-color: var(--accent-color, #6366f1);
          background: var(--accent-light, #f3f4ff);
        }

        .network-option.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .network-option-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
        }

        .network-icon {
          width: 32px;
          height: 32px;
          object-fit: contain;
        }

        .network-name {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--text-primary, #111);
          text-align: center;
        }

        .network-currency {
          font-size: 0.75rem;
          color: var(--text-secondary, #666);
          font-weight: 500;
        }

        .network-selected-indicator {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          color: var(--accent-color, #6366f1);
        }

        @media (max-width: 640px) {
          .network-selector-grid {
            grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
          }
          
          .network-option {
            padding: 0.75rem;
          }
        }
      `}</style>
    </div>
  );
};

export default NetworkSelector;